import { useState, Fragment } from "react";
import { BiChevronDown, BiChevronUp, BiSolidEditAlt } from "react-icons/bi";
import { FaRegEye } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";

const cellAlign = (a) =>
  a === "left" ? "text-left" : a === "right" ? "text-right" : "text-center";

export default function Table(props) {
  const {
    columns = [],
    data = [],
    noRecordsMessage = "No records found",
    showActions = true,
    onEdit,
    onDelete,
    checkbox,
    onView,
    renderExpansion,
    tableClassName = "",
    expandedRows: externalExpandedRows,
    onExpand,
    onPrint,
    rowIdField = "id", // Default row identity field
  } = props;
  const [internalExpandedRows, setInternalExpandedRows] = useState({});
  const [loadingAction, setLoadingAction] = useState({});

  const handleActionWrapper = async (actionFn, actionName, rowId, row) => {
    if (!actionFn) return;
    try {
      setLoadingAction((prev) => ({ ...prev, [`${rowId}-${actionName}`]: true }));
      await actionFn(row);
    } finally {
      setLoadingAction((prev) => ({ ...prev, [`${rowId}-${actionName}`]: false }));
    }
  };

  const ActionSpinner = () => (
    <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  const expandedRows = externalExpandedRows || internalExpandedRows;

  const toggleRow = (id) => {
    const newState = !expandedRows[id];
    if (onExpand) {
      onExpand(id, newState);
    }
    if (!externalExpandedRows) {
      setInternalExpandedRows((prev) => ({ ...prev, [id]: newState }));
    }
  };
  const cols = Array.isArray(columns) ? columns : [];
  const rows = Array.isArray(data) ? data : [];

  const idsOnPage = checkbox?.idField
    ? rows
        .map((r) => r?.[checkbox.idField])
        .filter((x) => x !== undefined && x !== null)
    : [];

  const allSelected =
    idsOnPage.length > 0 &&
    idsOnPage.every((id) => (checkbox?.selected ?? []).includes(id));

  return (
    <div className="relative bg-white border border-gray-200">
      <table className={`w-full text-left border-collapse ${tableClassName || "text-[12px]"}`}>
        {cols.some((c) => c.w) && (
          <colgroup>
            {checkbox && <col className="w-10" />}
            {cols.map((c, i) => (
              <col key={i} className={c.w ?? "w-auto"} />
            ))}
            {showActions && <col className="w-28" />}
          </colgroup>
        )}

        <thead className="bg-gray-50 text-gray-700 uppercase sticky top-0 z-0 border-b border-gray-200">
          <tr>
            {renderExpansion && <th className="w-10"></th>}
            {checkbox && (
              <th className="p-3 text-center align-middle border-r border-gray-200">
                <input
                  type="checkbox"
                  className="w-3.5 h-4 border-gray-300 text-primary focus:ring-primary cursor-pointer"
                  checked={allSelected}
                  onChange={(e) =>
                    checkbox?.onToggleAll?.(idsOnPage, e.target.checked)
                  }
                />
              </th>
            )}
            {cols.map((col, i) => (
              <th
                key={i}
                className={`p-3 text-[10px] font-black text-[#475569] uppercase tracking-[1.5px] border-r border-gray-200 last:border-r-0 ${cellAlign(
                  col.align,
                )} ${col.headerClassName || ""}`}
              >
                {col.label}
              </th>
            ))}
            {showActions && (
              <th className="p-3 text-[10px] font-black text-[#475569] uppercase tracking-[1.5px] text-center border-l border-gray-200">
                Actions
              </th>
            )}
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-200 text-gray-700">
          {rows.length > 0 ? (
            rows.map((row, idx) => {
              const rowId = row[rowIdField] ?? row?.pO_No ?? row?.PO_No ?? row?.dieId ?? row?.id ?? idx;
              const isExpanded = !!expandedRows[rowId];
              const customRowClass = props.getRowClassName ? props.getRowClassName(row, idx) : "";
              return (
                <Fragment key={row?.id ?? row?.key ?? idx}>
                  <tr
                    className={`hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${customRowClass}`}
                  >
                    {renderExpansion && (
                      <td className="p-2.5 text-center border-r border-gray-200">
                        <button
                          type="button"
                          onClick={() => toggleRow(rowId)}
                          className="text-primary hover:bg-gray-100 p-1"
                        >
                          {isExpanded ? (
                            <BiChevronUp size={18} />
                          ) : (
                            <BiChevronDown size={18} />
                          )}
                        </button>
                      </td>
                    )}
                    {checkbox && (
                      <td className="p-2.5 text-center border-r border-gray-200">
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 border-gray-300 text-primary focus:ring-primary cursor-pointer"
                          checked={(checkbox?.selected ?? []).includes(
                            row[checkbox.idField],
                          )}
                          onChange={(e) =>
                            checkbox?.onToggle?.(
                              row[checkbox.idField],
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                    )}

                    {cols.map((col, cidx) => {
                      const val = row[col.key];
                      const content =
                        col.type === "status" ? (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-tighter border ${
                              String(val).toLowerCase() === "done" ||
                              String(val).toLowerCase() === "active"
                                ? "bg-green-50 text-green-700 border-green-200"
                                : "bg-gray-50 text-gray-600 border-gray-200"
                            }`}
                          >
                            {val}
                          </span>
                        ) : col.render ? (
                          col.render(val, row, idx)
                        ) : (
                          val
                        );

                      return (
                        <td
                          key={cidx}
                          className={`p-2.5 align-middle whitespace-nowrap border-r border-gray-200 last:border-r-0 ${cellAlign(
                            col.align,
                          )} ${col.className || ""}`}
                        >
                          {content}
                        </td>
                      );
                    })}

                    {showActions && (
                      <td className="p-2 text-center border-l border-gray-200">
                        <div className="flex items-center justify-center gap-1">
                          {onEdit && (
                            <button
                              title="Edit"
                              className={`p-1.5 border border-gray-200 text-blue-600 hover:bg-blue-50 transition-all ${loadingAction[`${rowId}-edit`] ? 'opacity-50 cursor-not-allowed' : ''}`}
                              onClick={() => handleActionWrapper(onEdit, 'edit', rowId, row)}
                              disabled={loadingAction[`${rowId}-edit`]}
                            >
                              {loadingAction[`${rowId}-edit`] ? <ActionSpinner /> : <BiSolidEditAlt className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {onDelete && (
                            <button
                              title="Delete"
                              className={`p-1.5 border border-gray-200 text-red-600 hover:bg-red-50 transition-all ${loadingAction[`${rowId}-delete`] ? 'opacity-50 cursor-not-allowed' : ''}`}
                              onClick={() => handleActionWrapper(onDelete, 'delete', rowId, row)}
                              disabled={loadingAction[`${rowId}-delete`]}
                            >
                              {loadingAction[`${rowId}-delete`] ? <ActionSpinner /> : <MdDeleteForever className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {onView && (
                            <button
                              title="View"
                              className={`p-1.5 border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all ${loadingAction[`${rowId}-view`] ? 'opacity-50 cursor-not-allowed' : ''}`}
                              onClick={() => handleActionWrapper(onView, 'view', rowId, row)}
                              disabled={loadingAction[`${rowId}-view`]}
                            >
                              {loadingAction[`${rowId}-view`] ? <ActionSpinner /> : <FaRegEye className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {onPrint && (
                            <button
                              title="Print"
                              className={`p-1.5 border border-gray-200 text-green-600 hover:bg-green-50 transition-all ${loadingAction[`${rowId}-print`] ? 'opacity-50 cursor-not-allowed' : ''}`}
                              onClick={() => handleActionWrapper(onPrint, 'print', rowId, row)}
                              disabled={loadingAction[`${rowId}-print`]}
                            >
                              {loadingAction[`${rowId}-print`] ? <ActionSpinner /> : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  {isExpanded && renderExpansion && (
                    <tr>
                      <td
                        colSpan={
                          cols.length +
                          (showActions ? 1 : 0) +
                          (checkbox ? 1 : 0) +
                          (renderExpansion ? 1 : 0)
                        }
                        className="bg-gray-50 p-2 border-b border-gray-200"
                      >
                        {renderExpansion(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={
                  cols.length + (showActions ? 1 : 0) + (checkbox ? 1 : 0) + (renderExpansion ? 1 : 0)
                }
                className="text-center py-10 text-gray-400 font-medium"
              >
                {noRecordsMessage}
              </td>
            </tr>
          )}
        </tbody>
        {props.footer && (
          <tfoot className="sticky bottom-[-1px] z-10 bg-gray-50/90 backdrop-blur-sm border-t-2 border-gray-200 -2px_10px_rgba(0,0,0,0.02)]">
            <tr className="divide-x divide-gray-200 font-black text-slate-800">
               {renderExpansion && <td className="w-10"></td>}
               {checkbox && <td className="w-10"></td>}
               {cols.map((col, i) => (
                 <td key={i} className={`p-2.5 text-[11px] align-middle ${cellAlign(col.align)}`}>
                    {props.footer[col.key]}
                 </td>
               ))}
               {showActions && <td className="w-28"></td>}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
